<template>
    <div>
        <el-table
            ref="a2p-campaign-bundle-table"
            v-loading="loadingBundles"
            :data="bundles"
            class="w-full"
            stripe
        >
            <!-- Information Column -->
            <el-table-column
                prop="sid"
                label="Information"
                width="320"
            >
                <template v-slot="scope">
                    <InformationColumn
                        :row="scope.row"
                        :show-a2p-campaign="true"
                        :show-a2p-brand="true"
                        :show-a2p-profile-bundle="true"
                        :show-customer-profile="true"
                        name-field="friendly_name"
                    />
                </template>
            </el-table-column>

            <!-- Timestamps Column -->
            <el-table-column
                label="Timestamps"
                width="140"
            >
                <template v-slot="scope">
                    <TimestampsColumn v-if="scope.row.a2p_use_case" :row="scope.row.a2p_use_case" />
                    <span v-else>-</span>
                </template>
            </el-table-column>

            <!-- Use Case Column -->
            <el-table-column
                label="Use Case"
                prop="use_case"
                width="250"
            >
                <template v-slot="scope">
                    <UseCaseColumn
                        v-if="scope.row.a2p_use_case"
                        :row="scope.row.a2p_use_case"
                        :show-a2p-campaign="true"
                    />
                    <span v-else>-</span>
                </template>
            </el-table-column>

            <!-- Phone Numbers Column -->
            <el-table-column
                label="Phone Numbers"
                prop="incoming_numbers_count"
                width="150"
            >
                <template v-slot="scope">
                    <CountColumn 
                        :value="scope.row.incoming_numbers_count"
                        :max-value="scope.row.max_pool_size"
                    />
                </template>
            </el-table-column>

            <!-- Attempts Column -->
            <el-table-column
                label="Attempts"
                prop="attempts"
                width="120"
            >
                <template v-slot="scope">
                    <CountColumn
                        v-if="scope.row.a2p_use_case"
                        :value="scope.row.a2p_use_case.attempts || 0"
                        :single-value="true"
                    />
                    <span v-else>-</span>
                </template>
            </el-table-column>

            <!-- TCR ID Column -->
            <el-table-column
                label="TCR ID"
                prop="tcr_campaign_id"
                width="120"
            >
                <template v-slot="scope">
                    <span>{{ scope.row.a2p_use_case?.tcr_campaign_id }}</span>
                </template>
            </el-table-column>

            <!-- Status Column -->
            <el-table-column
                label="Status"
                prop="status_name"
                width="120"
            >
                <template v-slot="scope">
                    <StatusColumn
                        v-if="scope.row.a2p_use_case"
                        :row="scope.row.a2p_use_case"
                    />
                    <span v-else>-</span>
                </template>
            </el-table-column>

            <!-- Failure Reason Column -->
            <el-table-column
                label="Failure reason"
                prop="failure_reason"
            >
                <template v-slot="scope">
                    <FailureReasonColumn
                        v-if="scope.row.a2p_use_case"
                        :row="scope.row.a2p_use_case"
                        :is-a2p-campaign="true"
                    />
                    <span v-else>-</span>
                </template>
            </el-table-column>

            <!-- Actions Column -->
            <el-table-column
                label="Action"
                width="150"
            >
                <template v-slot="scope">
                    <!-- View Details Button -->
                    <div v-if="scope.row.a2p_use_case" class="mt-1">
                        <el-button
                            type="primary"
                            size="mini"
                            class="btn-block"
                            @click="handleViewDetails(scope.row.a2p_use_case)"
                        >
                            View details
                        </el-button>
                    </div>
                    <!-- Delete Button -->
                    <div class="mt-1">
                        <el-button
                            type="danger"
                            size="mini"
                            class="btn-block"
                            @click="handleDeleteBundle(scope.row)"
                            :loading="loadingDeleteBundle.includes(scope.row.id)"
                        >
                            Delete
                        </el-button>
                    </div>
                    <!-- Invoice Button -->
                    <div v-if="shouldShowInvoiceButton(scope.row)" class="mt-1">
                        <button
                            type="button"
                            class="btn btn-xs btn-block purple"
                            @click="openChargebeeInvoice(scope.row)"
                        >
                            <i class="fa fa-link"></i> See invoice
                        </button>
                    </div>
                </template>
            </el-table-column>
        </el-table>
    </div>
</template>

<script>
import { compliance_mixin, acl_mixin } from '../../../mixins'
import InformationColumn from '../columns/information-column'
import TimestampsColumn from '../columns/timestamps-column'
import UseCaseColumn from '../columns/use-case-column'
import CountColumn from '../columns/count-column'
import StatusColumn from '../columns/status-column'
import FailureReasonColumn from '../columns/failure-reason-column'

export default {
    name: 'A2pCampaignBundleTable',
    components: {
        InformationColumn,
        TimestampsColumn,
        UseCaseColumn,
        CountColumn,
        StatusColumn,
        FailureReasonColumn,
    },
    mixins: [compliance_mixin, acl_mixin],
    props: {
        loadingBundles: {
            type: Boolean,
            required: true,
        },
        company: {
            type: Object,
            required: true,
        },
        bundles: {
            type: Array,
            required: true,
        },
        loadingUpdateBundle: {
            type: Array,
            default: () => [],
        },
        loadingDeleteBundle: {
            type: Array,
            default: () => [],
        },
    },

    methods: {
        handleViewDetails(a2pUseCase) {
            this.$emit('view-details', a2pUseCase)
        },
        handleDeleteBundle(bundle) {
            this.$emit('delete-bundle', bundle)
        },
        getChargebeeInvoiceLink(a2p_registration) {
            let invoice_id = a2p_registration?.a2p_use_case?.chargebee_registration_invoice_id

            if (!invoice_id) {
                return false
            }

            let env = 'aloware'
            if (['local', 'dev', 'mde', 'testing', 'staging'].includes(localStorage.getItem('app_env'))) {
                env = 'aloware-test'
            }

            return `https://${env}.chargebee.com/admin-console/invoices/${invoice_id}`
        },
        openChargebeeInvoice(a2p_registration) {
            let link = this.getChargebeeInvoiceLink(a2p_registration)
            window.open(link, '_blank')
        },
        shouldShowInvoiceButton(a2p_registration) {
            return this.getChargebeeInvoiceLink(a2p_registration)
        },
    },
}
</script>
